import { FormEvent, useEffect, useState } from 'react';
import { X } from 'lucide-react';

export interface PosProfileFormValues {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PosProfileModalProps {
  open: boolean;
  displayName: string;
  initialName: string;
  initialEmail: string;
  saving?: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (values: PosProfileFormValues) => void | Promise<void>;
}

export function PosProfileModal({
  open,
  displayName,
  initialName,
  initialEmail,
  saving = false,
  error,
  onClose,
  onSubmit,
}: PosProfileModalProps) {
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setEmail(initialEmail);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setLocalError('');
  }, [open, initialName, initialEmail]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setLocalError('Le nom complet est requis');
      return;
    }
    if (!trimmedEmail || !trimmedEmail.includes('@')) {
      setLocalError('Adresse e-mail invalide');
      return;
    }

    if (newPassword || confirmPassword || currentPassword) {
      if (!currentPassword) {
        setLocalError('Mot de passe actuel requis pour changer le mot de passe');
        return;
      }
      if (newPassword.length < 6) {
        setLocalError('Nouveau mot de passe : minimum 6 caractères');
        return;
      }
      if (newPassword !== confirmPassword) {
        setLocalError('La confirmation ne correspond pas');
        return;
      }
    }

    await onSubmit({
      name: trimmedName,
      email: trimmedEmail,
      currentPassword,
      newPassword,
      confirmPassword,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pos-profile-title"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl border border-gray-700 bg-[#0C1821] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="pos-profile-title" className="text-lg font-semibold text-white">
              Mon profil
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Compte : <span className="text-gray-300">{displayName}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-800 hover:text-white"
            aria-label="Fermer"
            disabled={saving}
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Nom complet
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm text-white outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
              autoComplete="name"
              required
              disabled={saving}
            />
          </label>

          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            E-mail
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm text-white outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
              autoComplete="email"
              required
              disabled={saving}
            />
          </label>

          <div className="border-t border-gray-800 pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Mot de passe (optionnel)
            </p>
            <label className="mb-2 block text-[11px] text-gray-500">
              Mot de passe actuel
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm text-white outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
                autoComplete="current-password"
                disabled={saving}
              />
            </label>
            <label className="mb-2 block text-[11px] text-gray-500">
              Nouveau mot de passe
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm text-white outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
                autoComplete="new-password"
                minLength={6}
                disabled={saving}
              />
            </label>
            <label className="block text-[11px] text-gray-500">
              Confirmer le nouveau mot de passe
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm text-white outline-none focus:border-brand focus:ring-2 focus:ring-brand/30"
                autoComplete="new-password"
                minLength={6}
                disabled={saving}
              />
            </label>
          </div>
        </div>

        {(localError || error) && (
          <p className="mt-3 rounded-lg border border-red-900/50 bg-red-950/50 px-3 py-2 text-sm text-red-300">
            {localError || error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white"
            disabled={saving}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
}
